/**
 * IncontroConcluso — com'e' finita la mia ultima giornata calcolata.
 *
 * Christian 2026-11-03: tutte le partite della giornata (campionato E coppa,
 * se c'e'), sfogliabili come la card della prossima sfida / giornata in
 * corso: frecce ai lati all'altezza degli stemmi, trascinamento, pallini.
 * Prima si vedeva solo quella della prima competizione (la Serie A).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { PartitaItem } from "../../hooks/useFase5";
import { useSwipe } from "../../hooks/useSwipe";
import { LogoSquadra } from "../common/LogoSquadra";
import { tr } from "../../i18n";

type Esito = "vittoria" | "pareggio" | "sconfitta";

function esitoPer(p: PartitaItem, squadraId: string): Esito {
  const inCasa = p.squadra_casa_id === squadraId;
  const mieiGol = inCasa ? p.gol_casa : p.gol_trasferta;
  const loroGol = inCasa ? p.gol_trasferta : p.gol_casa;
  if (mieiGol > loroGol) return "vittoria";
  if (mieiGol < loroGol) return "sconfitta";
  return "pareggio";
}

interface Props {
  /** Le mie partite calcolate della giornata, nell'ordine delle competizioni. */
  partite: PartitaItem[];
  squadraId: string;
  numeroGiornata: number | null;
  nomeSquadra: (id: string) => string;
  nomeCompetizione: (id: string) => string;
}

export function IncontroConcluso({
  partite,
  squadraId,
  numeroGiornata,
  nomeSquadra,
  nomeCompetizione,
}: Props) {
  const [indice, setIndice] = useState(0);
  useEffect(() => {
    setIndice((i) => (i < partite.length ? i : 0));
  }, [partite.length]);
  const piuPartite = partite.length > 1;
  const vaiA = (delta: number) =>
    setIndice((i) => (i + delta + partite.length) % partite.length);
  const swipe = useSwipe({
    onAvanti: () => vaiA(1),
    onIndietro: () => vaiA(-1),
    attivo: piuPartite,
  });

  const p = partite[indice] ?? null;
  if (!p) return null;
  const esito = esitoPer(p, squadraId);

  return (
    <div>
      {/* Come la card della giornata: titolo e competizione sempre interi,
          piu' piccoli su telefono. */}
      <div className="flex justify-between items-baseline gap-2 mb-2 whitespace-nowrap">
        <h3 className="font-display text-[15px] sm:text-lg tracking-wide sm:tracking-wider text-fg">
          {tr("INCONTRO CONCLUSO")}
        </h3>
        <span className="text-[9px] sm:text-[10px] text-fg-muted uppercase font-mono">
          {nomeCompetizione(p.competizione_id)}
          {piuPartite ? tr(" · {{v1}}/{{v2}}", { v1: indice + 1, v2: partite.length }) : ""}
        </span>
      </div>

      <div className="relative">
        <div
          {...swipe.handlers}
          className="touch-pan-y select-none"
          style={{
            transform: `translateX(${swipe.offset}px)`,
            transition: swipe.inTrascinamento ? "none" : "transform 150ms",
          }}
        >
          <Link
            to={`/partita/${p.id}`}
            draggable={false}
            onClick={swipe.annullaClick}
            className={`block rounded-card p-4 border transition hover:scale-[1.01] ${
              esito === "vittoria"
                ? "bg-success/10 border-success/40"
                : esito === "sconfitta"
                  ? "bg-danger/10 border-danger/40"
                  : "bg-primary/10 border-primary/40"
            }`}
            title={tr("Apri dettaglio partita (formazioni + voti)")}
          >
            <div
              className={`text-center font-display text-xl mb-2 ${
                esito === "vittoria"
                  ? "text-success"
                  : esito === "sconfitta"
                    ? "text-danger"
                    : "text-info"
              }`}
            >
              {esito === "vittoria"
                ? tr("🏆 HAI VINTO!")
                : esito === "sconfitta"
                  ? tr("💔 Hai perso")
                  : tr("🤝 Pareggio")}
            </div>
            {/* Andrea 2026-10-30: stemmi sulla prima riga e nomi sulla
                seconda, cosi' un nome lungo che va a capo non sposta lo
                stemma. Il risultato occupa entrambe le righe. */}
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1">
              <div className="flex justify-center">
                <LogoSquadra squadraId={p.squadra_casa_id} size={44} />
              </div>
              <div className="row-span-2 text-center">
                <div className="font-display text-3xl tabular-nums">
                  {p.gol_casa} - {p.gol_trasferta}
                </div>
                <div className="text-[10px] text-fg-muted font-mono mt-0.5">
                  {Number(p.punteggio_casa).toFixed(1)} · {Number(p.punteggio_trasferta).toFixed(1)}{" "}
                  FP
                </div>
              </div>
              <div className="flex justify-center">
                <LogoSquadra squadraId={p.squadra_trasferta_id} size={44} />
              </div>
              <div className="text-center min-w-0 self-start font-display text-base text-fg leading-tight break-words">
                {nomeSquadra(p.squadra_casa_id)}
              </div>
              <div className="text-center min-w-0 self-start font-display text-base text-fg leading-tight break-words">
                {nomeSquadra(p.squadra_trasferta_id)}
              </div>
            </div>
            <div className="text-center text-[10px] text-fg-muted mt-2">
              {numeroGiornata !== null && (
                <span className="font-mono uppercase">
                  {tr("Giornata {{n}}", { n: numeroGiornata })} ·{" "}
                </span>
              )}
              <span className="underline">{tr("Vai al dettaglio →")}</span>
            </div>
          </Link>
        </div>

        {/* Frecce minimal all'altezza degli stemmi (padding + esito +
            meta' stemma), fuori dal <Link>. */}
        {piuPartite && (
          <>
            <button
              type="button"
              onClick={() => vaiA(-1)}
              aria-label={tr("Partita precedente")}
              title={tr("Partita precedente")}
              className="absolute left-0 top-[75px] -translate-y-1/2 w-7 h-10 flex items-center justify-center font-display text-3xl leading-none text-accent/80 hover:text-accent transition"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => vaiA(1)}
              aria-label={tr("Partita successiva")}
              title={tr("Partita successiva")}
              className="absolute right-0 top-[75px] -translate-y-1/2 w-7 h-10 flex items-center justify-center font-display text-3xl leading-none text-accent/80 hover:text-accent transition"
            >
              ›
            </button>
          </>
        )}
      </div>

      {piuPartite && (
        <div className="flex justify-center gap-1.5 mt-2">
          {partite.map((q, i) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setIndice(i)}
              aria-label={nomeCompetizione(q.competizione_id)}
              title={nomeCompetizione(q.competizione_id)}
              className={`h-1.5 rounded-full transition ${
                i === indice ? "w-6 bg-accent" : "w-3 bg-fg-muted/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
